<script setup lang="ts">
  import { Floating, Icon, IconName, IconSize, Typo, TypoVariant, Button } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, ref, watch } from 'vue';

  import type { AccountUser } from '../model/types';
  import { useHeaderAccount } from '../model/useHeaderAccount';
  import AccountHint from './AccountHint.vue';
  import AccountMenu from './AccountMenu.vue';
  import { ACCOUNT_TEXTS } from '../config/texts';

  interface HeaderAccountProps {
    user?: AccountUser | null;
    accountUrl?: string;
  }

  const props = withDefaults(defineProps<HeaderAccountProps>(), {
    user: null,
    accountUrl: '',
  });

  interface HeaderAccountEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
  }

  const emit = defineEmits<HeaderAccountEmits>();

  const core = useHeaderAccount({
    initialUser: props.user ?? null,
    initialAccountUrl: props.accountUrl ?? '',
  });

  const floatingRef = ref<{ hide: () => void } | null>(null);
  const overlayOpen = ref(false);

  const onChangeVisible = (visible: boolean): void => {
    overlayOpen.value = visible;
  };

  const onLogin = (): void => {
    if (!core.isAuthorized.value) {
      emit('login');
    }
  };

  const onLogout = (): void => {
    emit('logout');
    floatingRef.value?.hide();
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      floatingRef.value?.hide();
    }
  };

  watch(
    overlayOpen,
    (open) => {
      if (open) {
        document.addEventListener('keydown', onKeydown);
      } else {
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { immediate: true },
  );

  watch(
    () => props.user,
    (u) => core.setUser(u ?? null),
    { immediate: true },
  );

  watch(
    () => props.accountUrl,
    (url) => core.setAccountUrl(url ?? ''),
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
  });
</script>

<template>
  <div class="header-account" data-testid="header-account">
    <Floating
      :key="core.isAuthorized.value ? 'auth' : 'guest'"
      ref="floatingRef"
      trigger="hover"
      placement="bottom-end"
      :teleported="false"
      @change-visible="onChangeVisible"
    >
      <template #reference>
        <Button
          v-if="!core.isAuthorized.value"
          type="button"
          class="header-account__button"
          appearance="sub-primary"
          data-testid="header-account-login"
          icon-left="user"
          size="s"
          @click="onLogin"
        >
          {{ ACCOUNT_TEXTS.loginLabel }}
        </Button>
        <div v-else class="header-account__control-container">
          <div class="header-account__control" data-testid="header-account-user">
            <Typo :variant="TypoVariant.BodyMRegular">{{ core.displayName.value }}</Typo>
          </div>

          <Icon :class="['header-account__icon', {
            'header-account__icon-open': overlayOpen,
          }]" name="chevron-down"></Icon>
        </div>
      </template>

      <template #floating>
        <div>
          <AccountHint v-if="!core.isAuthorized.value" />
          <AccountMenu v-else :links="core.resolvedLinks.value" @logout="onLogout" />
        </div>
      </template>
    </Floating>
  </div>
</template>

<style scoped lang="scss">
  .header-account {
    &__button {
      color: var(--color-neutral-0);
      border: 1px solid var(--color-neutral-0);
    }
    &__icon {
      fill: var(--color-neutral-0);
      transition: var(--transition-default);
    }
    &__control-container {
      display: flex;
      gap: var(--spacing-xxs);
      align-items: center;
      cursor: pointer;
    }
    &__icon-open {
      transform: rotate(180deg);
    }
    &__control {
      width: 32px;
      height: 32px;
      border-radius: 100%;
      background-color: var(--color-bg-secondary);
      text-transform: uppercase;
      color: var(--color-text-secondary);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
